import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import Sidebar from './Sidebar';
import AuthModal from './AuthModal';
import {
  getStoredUserNo,
  hasSmartHealingOriginSession,
  isSmartHealingLinkedUser,
  isSmartHealingReferrer,
  markSmartHealingLinkedUser,
  markSmartHealingOriginSession,
} from '../utils/smartHealingAccess';

const LABELS = {
  menuOpen: '\uBA54\uB274 \uC5F4\uAE30',
  menuIcon: '\u2630',
  title: 'AI \uB2E5\uD130\uD050',
  smartHealingMenu: '\uC2A4\uB9C8\uD2B8\uD790\uB9C1',
  smartHealingAction: '\uC2A4\uB9C8\uD2B8\uD790\uB9C1 \uC5F0\uACB0',
  smartHealingError: '\uC2A4\uB9C8\uD2B8\uD790\uB9C1 \uC5F0\uACB0\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.',
  smartHealingLoading: '\uC5F0\uACB0\uC911',
  reports: '\uB9AC\uD3EC\uD2B8',
  logout: '\uB85C\uADF8\uC544\uC6C3',
  logoutLine1: '\uB85C\uADF8',
  logoutLine2: '\uC544\uC6C3',
  login: '\uB85C\uADF8\uC778',
};

function Layout({ context }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL || '';
  const gatewayApiUrl = /^https?:\/\//i.test(apiUrl) ? apiUrl : '';
  const smartHealingUrl = process.env.REACT_APP_SMART_HEALING_URL || 'http://localhost:3011';

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [smartHealingLoading, setSmartHealingLoading] = useState(false);
  const [smartHealingOrigin, setSmartHealingOrigin] = useState(() => hasSmartHealingOriginSession());

  const isLoggedIn = auth.isAuthenticated;
  const authInitializing = auth.isBooting;
  const currentUserNo = auth.userNo || getStoredUserNo();
  const showSmartHealingButton = smartHealingOrigin
    || (!authInitializing && isLoggedIn && isSmartHealingLinkedUser(currentUserNo));
  const showMenuButton = !authInitializing && (isLoggedIn || showSmartHealingButton);
  const isAuthOpen = Boolean(auth.loginModal);
  const authMessage = auth.loginModal?.message || '';

  const sidebarContext = useMemo(() => {
    return {
      ...(context || {}),
      isLoggedIn,
      isAdmin: auth.isAdmin || auth.adminStatus === 'granted',
      adminStatus: auth.adminStatus,
    };
  }, [auth.adminStatus, auth.isAdmin, context, isLoggedIn]);

  useEffect(() => {
    if (!smartHealingOrigin && isSmartHealingReferrer(document.referrer, smartHealingUrl)) {
      markSmartHealingOriginSession();
      setSmartHealingOrigin(true);
    }
  }, [smartHealingOrigin, smartHealingUrl]);

  useEffect(() => {
    setSmartHealingOrigin(hasSmartHealingOriginSession());
  }, [auth.isAuthenticated, auth.userNo]);

  const handleSmartHealingOpen = async () => {
    if (smartHealingLoading) {
      return;
    }

    if (!auth.accessToken) {
      window.location.href = smartHealingUrl;
      return;
    }

    setSmartHealingLoading(true);

    try {
      const { data } = await axios.post(
        `${gatewayApiUrl}/api/v1/smart-healing/bridge-token`,
        {},
        {
          headers: {
            Authorization: `Bearer ${auth.accessToken}`,
          },
        },
      );

      if (currentUserNo) {
        markSmartHealingLinkedUser(currentUserNo);
      }

      window.location.href = data?.launch_url || smartHealingUrl;
    } catch (error) {
      window.alert(error?.response?.data?.detail || LABELS.smartHealingError);
      setSmartHealingLoading(false);
    }
  };

  return (
    <div className="layout app-container">
      <Sidebar
        isOpen={isMenuOpen}
        closeMenu={() => setIsMenuOpen(false)}
        context={sidebarContext}
        showSmartHealingAction={showSmartHealingButton}
        onSmartHealingClick={() => {
          setIsMenuOpen(false);
          void handleSmartHealingOpen();
        }}
        smartHealingLoading={smartHealingLoading}
        smartHealingLabel={smartHealingLoading ? LABELS.smartHealingLoading : LABELS.smartHealingMenu}
      />

      <header
        className="app-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          position: 'relative',
          justifyContent: 'flex-end',
          height: 48,
          padding: '6px 12px',
        }}
      >
        {showMenuButton ? (
          <button
            type="button"
            aria-label={LABELS.menuOpen}
            onClick={() => setIsMenuOpen((open) => !open)}
            style={{
              position: 'absolute',
              left: 12,
              height: 32,
              width: 32,
              borderRadius: 10,
              border: '1px solid #e5e7eb',
              background: '#fff',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              lineHeight: 1,
            }}
          >
            {LABELS.menuIcon}
          </button>
        ) : null}

        <div
          className="header-title"
          role="button"
          tabIndex={0}
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            textAlign: 'center',
            fontWeight: 600,
            cursor: 'pointer',
          }}
          onClick={() => navigate('/')}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              navigate('/');
            }
          }}
        >
          {LABELS.title}
        </div>

        <div className="header-actions">
          {authInitializing ? null : isLoggedIn ? (
            <>
              <button
                type="button"
                onClick={() => navigate('/reports')}
                className="submit-button reports-btn compact-header-btn"
                aria-label={LABELS.reports}
              >
                <span className="full-label">{LABELS.reports}</span>
                <span className="short-label" aria-hidden="true">{LABELS.reports}</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  auth.actions.logout({ reason: 'user' });
                  setSmartHealingOrigin(false);
                  navigate('/', { replace: true });
                }}
                className="compact-header-btn logout-btn"
                style={{ border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer' }}
                aria-label={LABELS.logout}
              >
                <span className="two-line-label">
                  <span>{LABELS.logoutLine1}</span>
                  <span>{LABELS.logoutLine2}</span>
                </span>
              </button>
            </>
          ) : (
            <button
              type="button"
              className="login-button"
              onClick={() => auth.actions.openLoginModal()}
              style={{
                whiteSpace: 'nowrap',
                height: 32,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {LABELS.login}
            </button>
          )}
        </div>
      </header>

      <main className="main-content">
        <Outlet context={sidebarContext} />
      </main>

      <AuthModal
        isOpen={isAuthOpen}
        defaultMode="login"
        infoMessage={authMessage}
        onClose={() => {
          auth.actions.closeLoginModal();
        }}
        onSuccess={(data) => {
          if (smartHealingOrigin && data?.user_no) {
            markSmartHealingLinkedUser(data.user_no);
          }

          auth.actions.loginSuccess(data, {
            source: 'web',
            preserveExistingIntent: true,
          });

          const intent = auth.actions.consumeIntent();
          if (intent?.path) {
            navigate(intent.path, { replace: true });
          }

          return false;
        }}
      />
    </div>
  );
}

export default Layout;
